import { ClientDataType } from "@/api/ClientApi";
import { InvoiceDataType } from "@/api/InvoiceApi";
import { InvoiceProductDataType } from "@/api/InvoiceProductApi";
import { ProductDataType } from "@/api/ProductsApi";
import { SuscriptionDataType } from "@/api/SuscriptionsApi";
import { ApiEInvoiceRequest, ApiENoteRequest, ATVResponseBody, DocumentMetadata, EdocumentPaymentWay, InfoRefElement, MessageMetadata, OtrosCargosElement } from "./eInvoiceApi";
import { StoredOtherCharge, readDocumentOtherCharges, sumOtherChargesAmounts, toOtrosCargosElements } from "@utils/DocumentOtherCharges";
import { InvoiceRelatedAttributes } from "@/formTypes/InvoiceDataType";
import { ProductExemptionDataType } from "@/formTypes/ProductExemptionDataType";
import { SuscriptionRelatedAttributes } from "@/formTypes/ProfileFormDataType";
import { EDocumentCalculateFields } from "@/formTypes/eInvoice/InvoiceMetadaType";
import { TaxDetailProps, WithProductMetadata } from "@/formTypes/eInvoice/ProductMetadaType";
import { ATV_SERVICES_SIZING_UNID, CURRENCIES_CODES, EINVOICE_EXPORT_INVOICE_TYPE_CODE, INVOICE_CREDIT_TYPE_CODE, REFERENCES_CODES, REFERENCES_INFORMATION, SALE_CONDITIONS, SERVICE_PRODUCT_TYPE_CODE } from "@utils/Constants";
import { getFechaDeEmisionRFC3339, sortProductsByOrder } from "@utils/EDocument";
import { calculateTotalIVADevuelto, toEdocumentPaymentWays } from "@utils/IvaDevuelto";
import { normalizePhoneNumber } from "@utils/Phone";
import { resolveAtvCreditTerm, resolveSaleConditionCode } from "@utils/SaleCondition";
import { CR_CANTONS, CR_DISTRICTS, CR_NEIBOARD } from "@utils/LocationData";
import { omitProperties, removeFunctionFromObject, safeEncodeBase64, stripHtmlTags, toNumberFormat } from "@utils/Utils";
import { EDOCUMENT_TEXT_MAX_LENGTH, sanitizeSafeText } from "@amplifyShared/safeText";
import { roundCant } from "@utils/Cant";
import { EasyposEDocumentApi } from "./eInvoiceApi";
import { InvoicePaymentDataType } from "@/api/InvoicePaymentApi";
import { CreditnoteDataType } from "@/api/CreditnoteApi";
import { CreditnoteRelatedAttributes } from "@/formTypes/CreditnoteDataType";
import { EDocumentProductDataType } from "@/formTypes/EDocumentProductDataType";
import { DebitnoteDataType } from "@/api/DebitnoteApi";
import { BuyInvoiceDataType } from "@/api/BuyInvoiceApi";
import { buyInvoiceRelatedAttributes } from "@/formTypes/BuyInvoiceDataType";
import moment from "moment";

type EDocumentKind = "Creditnote" | "Debitnote" | "Invoice" | "BuyInvoice";

export const HACIENDA_CURRENT_VERSION = "4.4"
export const HACIENDA_PREVIOUS_VERSION = "4.3"

export class EasyposEInvoice extends EasyposEDocumentApi {
    constructor(protected suscription: SuscriptionDataType) {
        super(suscription)
    }

    async submitEInvoice(invoice: InvoiceDataType, references?: InfoRefElement[]): Promise<ATVResponseBody> {
        let response: ATVResponseBody = {
            status: -1,
            message: "La factura electronica no esta activada en esta suscripcion",
            awsResponse: ""
        }
        if (this.suscription.einvoice_status) {
            const requestBody: ApiEInvoiceRequest = this.createInvoiceObject(invoice, references);
            response = await this.submitDocument(requestBody, "", "post")
        } else {
            response.message = "La facturacion electronica no esta activa en la suscripcion"
        }
        return response;
    }

    async submitEBuyInvoice(invoice: BuyInvoiceDataType, references?: InfoRefElement[]): Promise<ATVResponseBody> {
        let response: ATVResponseBody = {
            status: -1,
            message: "La factura electronica de compra no esta activada en esta suscripcion",
            awsResponse: ""
        }
        if (this.suscription.einvoice_status) {
            const requestBody: ApiEInvoiceRequest = this.createBuyInvoiceObject(invoice, references);
            response = await this.submitDocument(requestBody, "", "post")
        } else {
            response.message = "La facturacion electronica de compra no esta activa en la suscripcion"
        }
        return response;
    }

    /**
     * Payload del ATV de una nota de credito, sin enviarlo. Lo usa el descuento por nota
     * de credito: la nota se guarda con este JSON en `pending_submission` y la lambda
     * submit-pending-enote lo envia cuando Hacienda ya registro la factura referenciada.
     */
    buildCreditnoteRequest(invoice: InvoiceDataType, creditNote: CreditnoteDataType, references?: InfoRefElement[]): ApiENoteRequest {
        return this.createENoteObject(invoice, creditNote, creditNote.creditnoteProducts as EDocumentProductDataType[], "Creditnote", references);
    }

    async submitCreditnote(invoice: InvoiceDataType, creditNote: CreditnoteDataType, references?: InfoRefElement[]): Promise<ATVResponseBody> {
        let response: ATVResponseBody = {
            status: -1,
            message: "La factura electronica no esta activada en esta suscripcion",
            awsResponse: ""
        }
        if (this.suscription.einvoice_status) {
            const requestBody: ApiENoteRequest = this.buildCreditnoteRequest(invoice, creditNote, references);
            response = await this.submitDocument(requestBody, "", "delete")
        } else {
            response.message = "La facturacion electronica no esta activa en la suscripcion"
        }
        return response;
    }

    async submitBuyInvoiceCreditnote(buyInvoice: BuyInvoiceDataType, creditNote: CreditnoteDataType, references?: InfoRefElement[]): Promise<ATVResponseBody> {
        let response: ATVResponseBody = {
            status: -1,
            message: "La factura electronica no esta activada en esta suscripcion",
            awsResponse: ""
        }
        if (this.suscription.einvoice_status) {
            const requestBody: ApiENoteRequest = this.createBuyInvoiceENoteObject(buyInvoice, creditNote, creditNote.creditnoteProducts as EDocumentProductDataType[], "Creditnote", references);
            response = await this.submitDocument(requestBody, "", "delete")
        } else {
            response.message = "La facturacion electronica no esta activa en la suscripcion"
        }
        return response;
    }

    async submitDebitnote(invoice: InvoiceDataType, debitnote: DebitnoteDataType, references?: InfoRefElement[]): Promise<ATVResponseBody> {
        let response: ATVResponseBody = {
            status: -1,
            message: "La factura electronica no esta activada en esta suscripcion",
            awsResponse: ""
        }
        if (this.suscription.einvoice_status) {
            const requestBody: ApiENoteRequest = this.createENoteObject(invoice, debitnote, debitnote.debitnoteProducts as EDocumentProductDataType[], "Debitnote", references);
            response = await this.submitDocument(requestBody, "", "delete")
        } else {
            response.message = "La facturacion electronica no esta activa en la suscripcion"
        }
        return response;
    }

    async submitMessage(messageBody: Partial<MessageMetadata>): Promise<ATVResponseBody> {

        let response: ATVResponseBody = {
            status: -1,
            message: "La facturacion electronica no esta activada en esta suscripcion",
            awsResponse: ""
        }
        if (this.suscription.einvoice_status) {
            response = await this.submitDocument(
                {
                    messageBody,
                    Suscription: this.getSuscriptionData(),
                    Metadata: messageBody.metadata
                }, "/messages", "post")
        } else {
            response.message = "La facturacion electronica no esta activada en esta suscripcion";
        }
        return response;
    }

    private createBuyInvoiceENoteObject(buyInvoice: BuyInvoiceDataType, creditNote: CreditnoteDataType | DebitnoteDataType, products: EDocumentProductDataType[], noteKind: EDocumentKind, references?: InfoRefElement[]): ApiENoteRequest {
        const calculatedValues = this.addEDocumentCalculateFields(products);
        const invoiceCalculatedValues = this.addEDocumentCalculateFields(buyInvoice.buyInvoiceProducts as EDocumentProductDataType[]);
        const formattedCreditnoteData = omitProperties(creditNote, [...CreditnoteRelatedAttributes]);
        const formattedInvoiceData = omitProperties(buyInvoice, [...buyInvoiceRelatedAttributes]);
        const clientData = this.getDocumentClientDataForBuyInvoice(buyInvoice);

        let requestBody: ApiENoteRequest = {
            InvoiceNote: {
                ...formattedCreditnoteData,
                email: `${buyInvoice.email || ""}`.split(","),
                ...calculatedValues,
                einvoice_kind: `1`,
                plazo: Number(buyInvoice.plazo || 0),
                description: stripHtmlTags(`${creditNote.description}`)
            },
            InvoiceNoteProduct: this.generateDocumentProducs(products),
            Invoice: {
                ...formattedInvoiceData,
                email: `${buyInvoice.email || ""}`.split(","),
                ...invoiceCalculatedValues
            },
            // Saleman: invoice.saleman,
            // InvoiceProduct: this.generateInvoiceProduct(invoice),
            Suscription: this.getSuscriptionData(),
            Metadata: this.generateEDocumentMetadata(
                noteKind, creditNote, "created", "document_key", buyInvoice.tipo || "0", creditNote.currency.currency_id,
                this.generatePaymentWays(buyInvoice.buyInvoicePayments as InvoicePaymentDataType[]), references
            ),
            Client: clientData,
        };
        return requestBody;
    }

    /**
     * Condicion de venta que se declara en el comprobante. Con `no_auto_cancel_contado`
     * el contado se cobra despues con un Recibo de Dinero, asi que se emite como credito;
     * el `tipo` del documento en la base de datos no cambia.
     */
    private resolveDocumentSaleCondition(invoice: InvoiceDataType): string {
        return resolveSaleConditionCode(
            invoice.tipo,
            invoice.es_apartado || false,
            this.suscription.no_auto_cancel_contado || false,
        );
    }

    /** True cuando el credito lo impone la suscripcion y no el tipo elegido en el documento. */
    private isForcedCreditCondition(invoice: InvoiceDataType): boolean {
        return this.resolveDocumentSaleCondition(invoice) == INVOICE_CREDIT_TYPE_CODE
            && `${invoice.tipo ?? ""}` != INVOICE_CREDIT_TYPE_CODE;
    }

    private getDocumentVersion(invoice: InvoiceDataType) {
        const invoiceData = invoice?.fecha;
        const invoiceFecha = moment(invoice?.fecha);
        let documentVersion = HACIENDA_CURRENT_VERSION;
        if (invoiceData && invoiceFecha.isSameOrBefore("2025-08-31 00:00:00")) {
            documentVersion = HACIENDA_PREVIOUS_VERSION
        }
        return documentVersion
    }

    private createENoteObject(invoice: InvoiceDataType, creditNote: CreditnoteDataType | DebitnoteDataType, products: EDocumentProductDataType[], noteKind: EDocumentKind, references?: InfoRefElement[]): ApiENoteRequest {
        const paymentWays = this.generatePaymentWays(invoice.invoicePayments as InvoicePaymentDataType[]);
        // El IVA Devuelto es inexistente en la factura de exportacion (columna FEE del anexo v4.4).
        const ivaDevueltoPaymentWays = invoice.einvoice_kind == EINVOICE_EXPORT_INVOICE_TYPE_CODE ? [] : paymentWays;
        // La nota arrastra los otros cargos de la factura que revierte (los hereda
        // `generateCreditFromInvoice`); una nota por monto los trae vacios a proposito.
        const noteOtherCharges = readDocumentOtherCharges(creditNote);
        const invoiceOtherCharges = readDocumentOtherCharges(invoice);
        const calculatedValues = this.addEDocumentCalculateFields(products, ivaDevueltoPaymentWays, noteOtherCharges);
        const invoiceCalculatedValues = this.addEDocumentCalculateFields(invoice.invoiceProducts as EDocumentProductDataType[], ivaDevueltoPaymentWays, invoiceOtherCharges);
        // `pending_submission` guarda una copia de este mismo payload: si no se omite, un
        // reenvio lo incrustaria dentro de si mismo y duplicaria el tamano del request.
        const formattedCreditnoteData = omitProperties(creditNote, [...CreditnoteRelatedAttributes, "pending_submission", "pending_submission_attempts"]);
        const formattedInvoiceData = omitProperties(invoice, [...InvoiceRelatedAttributes]);
        // La nota hereda la condicion de venta de la factura ligada: si la factura se emitio
        // como credito por no_auto_cancel_contado, la nota tiene que declarar lo mismo.
        const saleCondition = this.resolveDocumentSaleCondition(invoice);
        const forcedCredit = this.isForcedCreditCondition(invoice);
        const clientData = this.getDocumentClientData(invoice, forcedCredit);

        const documentVersion = this.getDocumentVersion(invoice);

        let metadata = this.generateEDocumentMetadata(
            noteKind, creditNote, "created", "document_key", saleCondition, creditNote.currency.currency_id,
            paymentWays, references, documentVersion, toOtrosCargosElements(noteOtherCharges)
        );

        if(documentVersion == HACIENDA_PREVIOUS_VERSION && saleCondition == INVOICE_CREDIT_TYPE_CODE){
            const medioPago = [{
                "tipo": "01"
            }];
        
            metadata={
                ...metadata,
                medioPago
            }
        }
           

        const fechaEmisionUTCFactura = getFechaDeEmisionRFC3339(invoice, "fecha")

        let requestBody: ApiENoteRequest = {
            InvoiceNote: {
                ...formattedCreditnoteData,
                email: `${invoice.email || ""}`.split(","),
                ...calculatedValues,
                einvoice_kind: `${invoice.einvoice_kind}`,
                plazo: resolveAtvCreditTerm(invoice.plazo, forcedCredit),
                description: stripHtmlTags(`${creditNote.description}`),
                Metadata: metadata
            },
            InvoiceNoteProduct: this.generateDocumentProducs(products),
            Invoice: {
                ...formattedInvoiceData,
                email: `${invoice.email || ""}`.split(","),
                plazo: resolveAtvCreditTerm(invoice.plazo, forcedCredit),
                ...invoiceCalculatedValues,
                Metadata: {
                    fechaEmisionUTC: fechaEmisionUTCFactura
                }
            },
            // Saleman: invoice.saleman,
            // InvoiceProduct: this.generateInvoiceProduct(invoice),
            Suscription: this.getSuscriptionData(),
            Metadata: metadata,
            Client: clientData,
        };
        return requestBody;
    }

    private createInvoiceObject(invoice: InvoiceDataType, references?: InfoRefElement[]): ApiEInvoiceRequest {
        const paymentWays = this.generatePaymentWays(invoice.invoicePayments as InvoicePaymentDataType[]);
        // El IVA Devuelto es inexistente en la factura de exportacion (columna FEE del anexo v4.4).
        const ivaDevueltoPaymentWays = invoice.einvoice_kind == EINVOICE_EXPORT_INVOICE_TYPE_CODE ? [] : paymentWays;
        const otherCharges = readDocumentOtherCharges(invoice);
        const calculatedValues = this.addEDocumentCalculateFields(invoice.invoiceProducts as EDocumentProductDataType[], ivaDevueltoPaymentWays, otherCharges);
        const formattedInvoiceData = omitProperties(invoice, [...InvoiceRelatedAttributes]);
        // Hacienda exige PlazoCredito cuando la condicion de venta es credito, y un contado
        // convertido a credito por la suscripcion normalmente lleva plazo 0.
        const forcedCredit = this.isForcedCreditCondition(invoice);
        const clientData = this.getDocumentClientData(invoice, forcedCredit);

        // El impuesto de servicio ya no se inventa como linea de producto: viaja como
        // cargo tipo 06 en <OtrosCargos> (ver readDocumentOtherCharges).
        const invoiceProducts: WithProductMetadata<EDocumentProductDataType>[] = this.generateDocumentProducs(invoice.invoiceProducts as EDocumentProductDataType[]);

        let requestBody: ApiEInvoiceRequest = {
            Invoice: {
                ...formattedInvoiceData,
                email: `${invoice.email || ""}`.split(","),
                plazo: resolveAtvCreditTerm(invoice.plazo, forcedCredit),
                ...calculatedValues
            },
            Saleman: invoice.saleman,
            InvoiceProduct: invoiceProducts,
            Suscription: this.getSuscriptionData(),
            Metadata: this.generateEDocumentMetadata(
                "Invoice", invoice, "fecha", "invoice_key", this.resolveDocumentSaleCondition(invoice), invoice.currency.currency_id,
                paymentWays, references, HACIENDA_CURRENT_VERSION, toOtrosCargosElements(otherCharges)
            ),
            Client: clientData,
        };
        return requestBody;
    }

    private createBuyInvoiceObject(invoice: BuyInvoiceDataType, references?: InfoRefElement[]): ApiEInvoiceRequest {
        const calculatedValues = this.addEDocumentCalculateFields(invoice.buyInvoiceProducts as EDocumentProductDataType[]);
        const formattedInvoiceData = omitProperties(invoice, [...buyInvoiceRelatedAttributes]);

        const invoiceProducts: WithProductMetadata<EDocumentProductDataType>[] = this.generateDocumentProducs(invoice.buyInvoiceProducts as EDocumentProductDataType[]);
        const cantonData = CR_CANTONS.find(c => c.canton_id == invoice.canton_id)
        const districtData = CR_DISTRICTS.find(d => d.district_id == invoice.district_id)

        let requestBody: ApiEInvoiceRequest = {
            Invoice: {
                ...formattedInvoiceData,
                email: `${invoice.email || ""}`.split(","),
                canton_id: (cantonData?.code || "").padStart(2, "0"),
                district_id: (districtData?.code || "").padStart(2, "0"),
                ...calculatedValues
            },
            InvoiceProduct: invoiceProducts,
            Suscription: this.getSuscriptionData(),
            Metadata: this.generateEDocumentMetadata(
                "BuyInvoice", invoice, "fecha", "invoice_key", invoice.tipo || "0", invoice.currency.currency_id,
                this.generatePaymentWays(invoice.buyInvoicePayments as InvoicePaymentDataType[]), references
            ),
        };
        return requestBody;
    }

    private getDocumentClientData(invoice: InvoiceDataType, forcedCredit = false): Partial<ClientDataType> {
        let clientData: Partial<ClientDataType> = {
            plazo: resolveAtvCreditTerm(invoice.client.plazo, forcedCredit),
            // "NumTelefono" es Integer en el anexo v4.4: sin guiones ni espacios.
            telefono1: normalizePhoneNumber(invoice.client.telefono1),
            telefono1_cd: normalizePhoneNumber(invoice.client.telefono1_cd),
            email: invoice.client.email,
        };
        return clientData
    }
    private getDocumentClientDataForBuyInvoice(buyInvoice: BuyInvoiceDataType): Partial<ClientDataType> {
        let clientData: Partial<ClientDataType> = {
            plazo: buyInvoice.provider.plazo,
            telefono1: normalizePhoneNumber(buyInvoice.provider.telefono1),
            telefono1_cd: normalizePhoneNumber(buyInvoice.provider.telefono1_cd),
            email: buyInvoice.provider.email,
        };
        return clientData
    }

    private addEDocumentCalculateFields(products: EDocumentProductDataType[], paymentWays: EdocumentPaymentWay[] = [], otherCharges: StoredOtherCharge[] = []): EDocumentCalculateFields {
        let initialData: EDocumentCalculateFields = {
            TotalServGravados: Number(0),
            TotalMercanciasGravadas: Number(0),
            TotalServExonerado: Number(0),
            TotalMercExonerada: Number(0),
            TotalMercanciasExentas: Number(0),
            TotalServExentos: Number(0),
            TotalImpAsumEmisorFabrica: Number(0),
            TotalIVADevuelto: Number(0),
            TotalMercNoSujeta: Number(0),
            // Suma de los montos ya resueltos: tiene que cuadrar exactamente con los nodos
            // <OtrosCargos> que emite la lambda, porque Hacienda valida esa igualdad.
            TotalOtrosCargos: sumOtherChargesAmounts(otherCharges),
            TotalServNoSujeto: Number(0)
        }
        const ventaNeta = products?.reduce(
            (total: number, ip: EDocumentProductDataType) => total + Number(ip.subtotal || 0) - Number(ip.descuento || 0),
            Number(0)
        ) || Number(0);
        initialData.TotalIVADevuelto = calculateTotalIVADevuelto(products, paymentWays, ventaNeta);

        return products?.reduce((a: EDocumentCalculateFields, ip: EDocumentProductDataType) => {
            const isServiceProduct = ATV_SERVICES_SIZING_UNID.includes(`${ip.unidad_medida}`) || ip.tipo_producto == SERVICE_PRODUCT_TYPE_CODE;
            let lineSubtotal = Number(ip.subtotal || 0);
            let exemption: ProductExemptionDataType;
            if (ip.exemption) {
                exemption = typeof ip.exemption == "string" ? JSON.parse(ip.exemption) : ip.exemption;
                let totalExoneradoLinea = lineSubtotal * (Number(exemption.porcentaje_compra || 0) / 13);
                totalExoneradoLinea = Number(toNumberFormat(totalExoneradoLinea));
                lineSubtotal -= totalExoneradoLinea;
                if (isServiceProduct) {
                    a.TotalServExonerado += totalExoneradoLinea
                } else {
                    a.TotalMercExonerada += totalExoneradoLinea
                }
            }
            if (ip.exento || ip.impuestos == 0) {
                if (isServiceProduct) {
                    a.TotalServExentos += lineSubtotal
                } else {
                    a.TotalMercanciasExentas += lineSubtotal
                }
            } else {
                if (isServiceProduct) {
                    a.TotalServGravados += lineSubtotal
                } else {
                    a.TotalMercanciasGravadas += lineSubtotal
                }
            }

            return a
        }, initialData)
    }

    /**
     * Las lineas llegan desde la relacion de Amplify (`invoiceProducts.*`), que resuelve contra un
     * GSI sin sort key (`gsi-Invoice.invoiceProducts`): DynamoDB las devuelve en un orden arbitrario
     * que no tiene relacion con el que capturó el usuario. Las vistas de impresión ya ordenan por
     * `orderIndex`, así que el payload del ATV tiene que ordenar igual: el `NumeroLinea` del XML sale
     * del indice del array (CreateInvoiceXml -> addDetalleServicio), y sin este sort el XML queda con
     * las lineas en otro orden que la factura del sistema.
     */
    private generateDocumentProducs(product: EDocumentProductDataType[]): WithProductMetadata<EDocumentProductDataType>[] {
        const invoiceProducts = Array();

        sortProductsByOrder(product).forEach((ip: EDocumentProductDataType) => {
            let exemption: ProductExemptionDataType | undefined = undefined;
            let taxExemptiontotal = 0
            if (ip.exemption) {
                exemption = (typeof ip.exemption == "string" ? JSON.parse(ip.exemption) : ip.exemption) as ProductExemptionDataType;
                exemption.fecha_emision = getFechaDeEmisionRFC3339(exemption, "fecha_emision")
                taxExemptiontotal = Number(exemption.monto_impuesto || 0)
            }

            const taxMetadata: TaxDetailProps = {
                codigo: `${ip.tipo_impuesto}`, //IVA
                codigoTarifa: `${ip.codigo_tarifa}`,
                tarifa: `${ip.porcentaje_impuesto}`,
                monto: `${ip.impuestos}`,
                Exemption: exemption,
            }

            const productData: Partial<ProductDataType> = {
                codigo: ip.product?.codigo,
                codigo_cabys: ip.product?.codigo_cabys
            }

            let filteredInvoiceProduct = removeFunctionFromObject(omitProperties(ip, ["exemption", "product"]))
            let product: WithProductMetadata<EDocumentProductDataType> = {
                ...filteredInvoiceProduct,
                // <Detalle> admite máximo 160 caracteres (XSD Hacienda v4.4) y no acepta
                // los caracteres que rompen la serialización del payload hacia AWS.
                description: sanitizeSafeText(ip.description, EDOCUMENT_TEXT_MAX_LENGTH),
                // <Cantidad> admite máximo 3 decimales (XSD Hacienda v4.4)
                cant: roundCant(Number(ip.cant || 0)),
                impuestoNeto: (Number(ip.impuestos || 0) - taxExemptiontotal),
                Product: productData,
                Metadata: {
                    Impuestos: [taxMetadata]
                }
            }
            invoiceProducts.push(product)
        })
        return invoiceProducts
    }

    private generateInvoiceProduct(invoice: InvoiceDataType): WithProductMetadata<InvoiceProductDataType>[] {
        const invoiceProducts = Array();

        invoice.invoiceProducts?.forEach((ip: InvoiceProductDataType) => {
            let exemption: ProductExemptionDataType | undefined = undefined;
            let taxExemptiontotal = 0
            if (ip.exemption) {
                exemption = (typeof ip.exemption == "string" ? JSON.parse(ip.exemption) : ip.exemption) as ProductExemptionDataType;
                exemption.fecha_emision = getFechaDeEmisionRFC3339(exemption, "fecha_emision")
                taxExemptiontotal = Number(exemption.monto_impuesto || 0)
            }

            const taxMetadata: TaxDetailProps = {
                codigo: `${ip.tipo_impuesto}`, //IVA
                codigoTarifa: `${ip.codigo_tarifa}`,
                tarifa: `${ip.porcentaje_impuesto}`,
                monto: `${ip.impuestos}`,
                Exemption: exemption,
            }

            const productData: Partial<ProductDataType> = {
                codigo: ip.product?.codigo,
                codigo_cabys: ip.product?.codigo_cabys
            }

            let filteredInvoiceProduct = removeFunctionFromObject(omitProperties(ip, ["exemption", "product"]))
            let product: WithProductMetadata<InvoiceProductDataType> = {
                ...filteredInvoiceProduct,
                // <Detalle> admite máximo 160 caracteres (XSD Hacienda v4.4) y no acepta
                // los caracteres que rompen la serialización del payload hacia AWS.
                description: sanitizeSafeText(ip.description, EDOCUMENT_TEXT_MAX_LENGTH),
                // <Cantidad> admite máximo 3 decimales (XSD Hacienda v4.4)
                cant: roundCant(Number(ip.cant || 0)),
                impuestoNeto: (Number(ip.impuestos || 0) - taxExemptiontotal),
                Product: productData,
                Metadata: {
                    Impuestos: [taxMetadata]
                }
            }
            invoiceProducts.push(product)
        })

        return invoiceProducts
    }

    private generateEDocumentMetadata<T extends { sequence?: string | null }>
        (
            documentType: EDocumentKind, document: T, dateField: keyof T, documentKeyField: keyof T, saleCondition: string = "0",
            currency_id: number, paymentMethod: EdocumentPaymentWay[], references: InfoRefElement[] = [], documentVersion: string = HACIENDA_CURRENT_VERSION,
            otrosCargos: OtrosCargosElement[] = []

        ): DocumentMetadata {
        let atvEndpoint = "https://api-sandbox.comprobanteselectronicos.go.cr/recepcion/v1";
        if (!this.suscription.einvoice_sandbox) {
            atvEndpoint = "https://api.comprobanteselectronicos.go.cr/recepcion/v1";
        }

        const fechaEmisionUTC = getFechaDeEmisionRFC3339(document, dateField)
        let documentMetadata: DocumentMetadata = {
            fechaEmisionUTC: fechaEmisionUTC,
            calltime: Date.now(),
            documentType: documentType,
            condicionVenta: SALE_CONDITIONS[Number(saleCondition) as keyof typeof SALE_CONDITIONS],
            clave: `${document[documentKeyField]}`,
            numeroConsecutivo: `${document.sequence}`,
            atvEndpoint: atvEndpoint,
            InfoRef: references,
            medioPago: paymentMethod,
            codigoMoneda: CURRENCIES_CODES[currency_id],
            documentVersion: documentVersion,
            OtrosCargos: otrosCargos
        }

        return documentMetadata
    }

    private generatePaymentWays<T extends { formaPago?: string, vuelto?: number, monto?: number }>
        (documentPaymentWays: T[]): EdocumentPaymentWay[] {
        // needed for v4.3
        // if (invoice.tipo == INVOICE_CREDIT_TYPE_CODE || invoice.invoicePayments?.length == 0) {
        //     paymentWays.push({
        //         tipo: PAYMENT_METHODS[INVOICE_CREDIT_TYPE_CODE].atvCode,
        //         monto: Number(invoice.total || 0)
        //     })
        // }
        // El anexo v4.4 permite hasta 4 medios de pago por comprobante.
        return toEdocumentPaymentWays(documentPaymentWays).slice(0, 4);
    }

    crearInformacionRefencia(fechaEmision: string, tipoReferencia: keyof typeof REFERENCES_INFORMATION, razon: string, codigoReferencia: keyof typeof REFERENCES_CODES, numeroReferencia: string): InfoRefElement {
        return {
            numero: numeroReferencia,
            fechaEmisionUTC: fechaEmision,
            tipoDoc: REFERENCES_INFORMATION[tipoReferencia],
            codigo: REFERENCES_CODES[codigoReferencia],
            razon: razon
        }
    }

    private getSuscriptionData(): Partial<SuscriptionDataType> {
        const cantonData = CR_CANTONS.find(c => c.canton_id == this.suscription.canton_id)
        const districtData = CR_DISTRICTS.find(d => d.district_id == this.suscription.district_id)
        const neiboardData = CR_NEIBOARD.find(n => n.neabord_id == this.suscription.neiboard_id)

        const suscriptionParseData = {
            ...this.suscription,
            id: `${this.suscription.numero}`,
            // "NumTelefono" es Integer en el anexo v4.4: sin guiones ni espacios.
            telefono1: normalizePhoneNumber(this.suscription.telefono1),
            telefono1_cd: normalizePhoneNumber(this.suscription.telefono1_cd),
            telefono2: normalizePhoneNumber(this.suscription.telefono2),
            telefono2_cd: normalizePhoneNumber(this.suscription.telefono2_cd),
            invoice_email: safeEncodeBase64(this.suscription.invoice_email || ""),
            creditnote_email: safeEncodeBase64(this.suscription.creditnote_email || ""),
            debitnote_email: safeEncodeBase64(this.suscription.debitnote_email || ""),
            canton_id: (cantonData?.code || "").padStart(2, "0"),
            district_id: (districtData?.code || "").padStart(2, "0"),
            neiboard_id: (neiboardData?.code || "").padStart(2, "0"),
        }

        return omitProperties(suscriptionParseData, [...SuscriptionRelatedAttributes,
            "qoute_email",
            "providerorder_email",
            "recibe_email",
            "settlement_email",
            "clientstatus_email",
            "order_email",
            "consignment_email",
            "hold_info"
        ], true);

    }
}
